﻿import { useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router';

import { Gaveta } from '@/components/Gaveta/Gaveta';
import { GrupoDeCampos } from '@/features/acoes/componentes/GrupoDeCampos';
import type { ContextoDaListaDeAcoes } from '@/features/acoes/contextoDaLista';

import { SeloDeImpedimento, SeloDePrazo, SeloDeSituacao } from '@/features/acoes/componentes/Selos';
import {
  useAcao,
  useCorrigirAssunto,
  useDefinirDadosDeProduto,
  useDefinirPrazo,
  useDefinirPrioridade,
  useDefinirResponsavel,
  useDefinirWorkItem,
  useEditarOrigem,
  useTransicao,
} from '@/features/acoes/hooks/useAcoes';
import { diasAte } from '@/features/acoes/prazo';
import { useResolvedores } from '@/features/referencias/useReferencias';
import {
  areasSesmtEmOrdem,
  modulosEmOrdem,
  rotuloDaAreaSesmt,
  rotuloDaComplexidade,
  rotuloDaJustificativa,
  rotuloDaPrioridade,
  rotuloDaTransicao,
  rotuloDoModulo,
  transicoesQueExigemJustificativa,
} from '@/features/acoes/rotulos';
import { ErroDeApi, descreverErro } from '@/lib/erros';
import { formatarData, formatarDataHora } from '@/lib/formato';
import type {
  AcaoDetalhada,
  AreaSesmt,
  ComplexidadeAcao,
  ModuloDoSistema,
  PrioridadeAcao,
  TipoTransicao,
} from '@/types/api';

import estilos from './GavetaDaAcao.module.css';

/**
 * Detalhe de uma acao, em gaveta sobre a lista. Especificacao V3.1, item 7.
 *
 * OS BOTOES VEM DE `transicoesPermitidas`, e nao de um calculo local. Reimplementar a
 * maquina de estados no cliente criaria duas versoes da mesma regra, e a divergencia
 * apareceria como botao que responde 409 — ou 403, quando a divergencia e de permissao.
 * O servidor cruza as duas coisas: situacao e permissao do usuario.
 *
 * GAVETA, E NAO TELA CHEIA. A lista fica montada atras, entao fechar nao refaz a consulta
 * e nao perde filtro nem pagina, e uma transicao ja atualiza a linha atras do painel.
 * /acoes/3 continua sendo URL de verdade.
 *
 * Fora do escopo desta entrega: edicao por grupo de campo, comentarios, historico e
 * referencias. Os endpoints existem; as telas entram nas fases seguintes.
 */

export function GavetaDaAcao() {
  const parametros = useParams();
  const navegar = useNavigate();
  const { aoTransicionar } = useOutletContext<ContextoDaListaDeAcoes>();
  const id = Number(parametros['id']);

  const { data: acao, error, isPending } = useAcao(id);

  const fechar = () => {
    // `..` e nao '/acoes': a gaveta e filha dos dois paineis, e quem abriu a acao a partir
    // da fila precisa voltar para a fila, com o recorte e o filtro dela.
    //
    // `replace` para a gaveta nao empilhar no historico: quem fecha espera voltar para
    // onde estava antes de abrir a acao, nao reabrir a acao.
    void navegar('..', { replace: true, relative: 'path' });
  };

  /*
    404 aqui significa "nao existe OU nao e do seu escopo", e o servidor nao distingue de
    proposito: responder 403 para "existe em outra area" confirmaria a existencia e
    permitiria mapear o que outras areas tratam. O texto acompanha essa escolha em vez de
    afirmar que a acao nao existe.
  */
  const naoEncontrada = error instanceof ErroDeApi && error.status === 404;

  const titulo = acao === undefined ? 'Ação' : `#${String(acao.numero)} ${acao.assunto}`;

  return (
    <Gaveta titulo={titulo} largura="larga" aoFechar={fechar}>
      {Number.isNaN(id) ? (
        <p className={estilos.aviso}>Endereço inválido.</p>
      ) : isPending ? (
        <p className={estilos.aviso}>Carregando ação...</p>
      ) : error !== null ? (
        <p className={estilos.falha} role="alert">
          {naoEncontrada
            ? 'Esta ação não existe ou não está no seu escopo de área.'
            : descreverErro(error)}
        </p>
      ) : (
        <Detalhe acao={acao} aoTransicionar={aoTransicionar} />
      )}
    </Gaveta>
  );
}

interface DetalheProps {
  acao: AcaoDetalhada;
  /**
   * Relata a transicao para a lista atras da gaveta.
   *
   * A gaveta nao decide se ha algo a avisar: ela nao conhece o recorte do painel nem o
   * filtro em uso. Quem cruza isso e a lista.
   */
  aoTransicionar: (acao: AcaoDetalhada) => void;
}

function Detalhe({ acao, aoTransicionar }: DetalheProps) {
  const transicao = useTransicao(acao.id);
  const { nomeDaArea, nomeDoUsuario, urlDoWorkItem } = useResolvedores();

  const [pendente, definirPendente] = useState<TipoTransicao | null>(null);
  const [justificativa, definirJustificativa] = useState('');

  const aplicar = (tipo: TipoTransicao) => {
    // Transicao que exige motivo abre o campo em vez de enviar. A regra e do
    // servidor; aqui ela apenas evita um 409 previsivel.
    if (transicoesQueExigemJustificativa.has(tipo) && pendente !== tipo) {
      definirPendente(tipo);
      definirJustificativa('');

      return;
    }

    transicao.mutate(
      {
        transicao: tipo,
        ...(justificativa.trim() === '' ? {} : { justificativa: justificativa.trim() }),
      },
      {
        onSuccess: (atualizada) => {
          definirPendente(null);
          definirJustificativa('');

          // A gaveta NAO fecha: a resposta ja traz a acao no estado novo, e ver o selo
          // mudar e a confirmacao mais direta. O aviso da lista serve para depois de
          // fechar, quando a linha nao estiver mais la.
          aoTransicionar(atualizada);
        },
      },
    );
  };

  return (
    <div className={estilos.conteudo}>
      {/*
        A classificacao do prazo vem do servidor, em `saudeDoPrazo`. Calcular aqui
        exigiria conhecer a janela de atencao de `negocio.janelaVencimentoDias`, que e
        configuracao de servidor — e um valor inventado mostraria "no prazo" numa acao
        vencida.
      */}
      <div className={estilos.selos}>
        <SeloDeSituacao situacao={acao.situacao} />
        <SeloDePrazo
          saude={acao.saudeDoPrazo}
          prazo={acao.prazo}
          diasRestantes={diasAte(acao.prazo)}
        />
        <SeloDeImpedimento visivel={acao.estaImpedida} />
        {acao.ehImportada && <span className={estilos.rotulo}>Importada da planilha</span>}
      </div>

      <section className={estilos.cartao}>
        <dl className={estilos.grade}>
          <Campo rotulo="Área responsável" valor={nomeDaArea(acao.areaResponsavelId)} />
          <Campo rotulo="Área solicitante" valor={nomeDaArea(acao.areaSolicitanteId)} />
          <Campo rotulo="Clientes afetados" valor={String(acao.qtdClientes)} />
          <CampoDoWorkItem numero={acao.workItem} url={urlDoWorkItem(acao.workItem)} />
          <Campo rotulo="Criada em" valor={formatarDataHora(acao.criadoEm)} />
          <Campo rotulo="Atualizada em" valor={formatarDataHora(acao.atualizadoEm)} />
        </dl>
      </section>

      {/*
        Responsavel, Prioridade e Prazo saíram do cartão de leitura fixo acima e viraram
        parte do grupo Processo: eram exibidos mas nunca editáveis — a tela tinha os
        endpoints no servidor desde antes, mas nenhuma UI chamava. O layout do grupo
        depende de `tipoAreaResponsavel`: área Produto mostra os 6 campos extras (PO, Dev,
        Qa, Complexidade, Financiada, Encaixe); qualquer outro tipo mostra só Responsável
        e Prazo, exatamente como o pedido do produto especificou.
      */}
      <GrupoDeCampos acao={acao} grupo="Processo">
        {(editavel) =>
          editavel ? (
            <FormularioDeProcesso key={acao.atualizadoEm} acao={acao} />
          ) : (
            <dl className={estilos.grade}>
              <Campo rotulo="Responsável" valor={nomeDoUsuario(acao.responsavelId)} />
              <Campo rotulo="Prazo" valor={formatarData(acao.prazo)} />
              {acao.tipoAreaResponsavel === 'Produto' && (
                <>
                  <Campo
                    rotulo="Prioridade"
                    valor={acao.prioridade === null ? '—' : rotuloDaPrioridade[acao.prioridade]}
                  />
                  <Campo rotulo="PO" valor={acao.po ?? '—'} />
                  <Campo rotulo="Dev" valor={acao.dev ?? '—'} />
                  <Campo rotulo="Qa" valor={acao.qa ?? '—'} />
                  <Campo
                    rotulo="Complexidade"
                    valor={
                      acao.complexidade === null ? '—' : rotuloDaComplexidade[acao.complexidade]
                    }
                  />
                  <Campo rotulo="Financiada" valor={acao.financiada ? 'Sim' : 'Não'} />
                  <Campo rotulo="Encaixe" valor={acao.encaixe ? 'Sim' : 'Não'} />
                </>
              )}
            </dl>
          )
        }
      </GrupoDeCampos>

      {/*
        A edicao fica dentro do grupo porque e ele quem sabe se aceita escrita agora e,
        quando nao aceita, por que. A lista de clientes afetados nao entra aqui: a
        projecao detalhada (`AcaoDetalhada`) so traz `qtdClientes`, nao os ids, e editar
        a lista exigiria o backend trazer os ids tambem — fora do escopo desta tela.
      */}
      <GrupoDeCampos acao={acao} grupo="Origem">
        {(editavel) =>
          editavel ? (
            <FormularioDeEdicaoDeOrigem key={acao.atualizadoEm} acao={acao} />
          ) : (
            <>
              <div>
                <h4 className={estilos.subsecao}>Descrição</h4>
                <Texto conteudo={acao.descricao} ausente="Sem descrição registrada." />
              </div>

              <div>
                <h4 className={estilos.subsecao}>Plano de ação</h4>
                <Texto conteudo={acao.planoAcao} ausente="Sem plano de ação registrado." />
              </div>

              <dl className={estilos.grade}>
                <Campo
                  rotulo="Área SESMT"
                  valor={acao.areaSesmt === null ? '—' : rotuloDaAreaSesmt[acao.areaSesmt]}
                />
                <Campo
                  rotulo="Módulo"
                  valor={acao.modulo === null ? '—' : rotuloDoModulo[acao.modulo]}
                />
              </dl>
            </>
          )
        }
      </GrupoDeCampos>

      {/* Grupo proprio, separado de Processo: fecha mais cedo (Backlog e
          EmDesenvolvimento), quando Responsavel/Prioridade/Prazo continuam abertos.
          Quem decide a janela e RegrasDeEdicao, no dominio; a tela apenas obedece
          `gruposEditaveis`. */}
      <GrupoDeCampos acao={acao} grupo="Task">
        {(editavel) => (
          // `key` no valor gravado: quando o servidor confirma a troca, o componente
          // remonta e o campo passa a refletir o novo numero. A alternativa seria um efeito
          // copiando prop em estado, que produz um render extra e deixa o campo
          // desalinhado do valor real enquanto a resposta chega.
          <CampoDeTask key={acao.workItem ?? 'sem-task'} acao={acao} editavel={editavel} />
        )}
      </GrupoDeCampos>

      {acao.motivoCancelamento !== null && (
        <section className={estilos.cartao}>
          <h3 className={estilos.secao}>Motivo do cancelamento</h3>
          <Texto conteudo={acao.motivoCancelamento} ausente="" />
        </section>
      )}

      <section className={estilos.cartao}>
        <h3 className={estilos.secao}>O que pode ser feito agora</h3>

        {acao.transicoesPermitidas.length === 0 ? (
          <p className={`${estilos.texto} ${estilos.vazio}`}>
            Esta ação está encerrada e não admite novas transições.
          </p>
        ) : (
          <div className={estilos.acoes}>
            {acao.transicoesPermitidas.map((tipo) => (
              <button
                key={tipo}
                type="button"
                className={
                  tipo === 'Cancelar'
                    ? `${estilos.botao} ${estilos.botaoDestrutivo}`
                    : estilos.botao
                }
                disabled={transicao.isPending}
                onClick={() => {
                  aplicar(tipo);
                }}
              >
                {rotuloDaTransicao[tipo]}
              </button>
            ))}
          </div>
        )}

        {pendente !== null && (
          <div className={estilos.justificativa}>
            <label className={estilos.rotulo} htmlFor="justificativa">
              {rotuloDaJustificativa(pendente)}
            </label>
            <textarea
              id="justificativa"
              className={estilos.area}
              value={justificativa}
              autoFocus
              onChange={(evento) => {
                definirJustificativa(evento.target.value);
              }}
            />
            <div className={estilos.acoes}>
              <button
                type="button"
                className={estilos.botao}
                disabled={justificativa.trim() === '' || transicao.isPending}
                onClick={() => {
                  aplicar(pendente);
                }}
              >
                {transicao.isPending ? 'Enviando...' : `Confirmar ${rotuloDaTransicao[pendente]}`}
              </button>
              <button
                type="button"
                className={estilos.botao}
                onClick={() => {
                  definirPendente(null);
                  definirJustificativa('');
                }}
              >
                Desistir
              </button>
            </div>
          </div>
        )}

        {transicao.error !== null && (
          <p className={estilos.falha} role="alert">
            {descreverErro(transicao.error)}
          </p>
        )}
      </section>
    </div>
  );
}

interface CampoProps {
  rotulo: string;
  valor: string;
}

function Campo({ rotulo, valor }: CampoProps) {
  return (
    <div>
      <dt className={estilos.rotulo}>{rotulo}</dt>
      <dd className={estilos.valor}>{valor}</dd>
    </div>
  );
}

interface FormularioDeEdicaoDeOrigemProps {
  acao: AcaoDetalhada;
}

/**
 * Edicao do conteudo de origem: assunto, descricao, plano de acao e area solicitante.
 *
 * SO ENTRA QUANDO O GRUPO ESTA EDITAVEL — o chamador (`GavetaDaAcao`) decide isso e so
 * monta este componente nesse caso. Por isso nao ha ramo de leitura aqui: ele vive no
 * chamador, ao lado do `<GrupoDeCampos>`.
 *
 * SEM VALIDACAO DE "AO MENOS UM CLIENTE": este formulario nao toca a lista de clientes.
 * A projecao detalhada (`AcaoDetalhada`) traz so `qtdClientes`, nao os ids; editar a
 * lista exigiria o backend trazer os ids tambem, e fica para quando essa tela existir.
 *
 * `key={acao.atualizadoEm}` no componente pai remonta este formulario quando o servidor
 * confirma a gravacao, pelo mesmo motivo de `CampoDeTask`: sincronizar estado local por
 * efeito produziria um render extra e deixaria o campo desalinhado do valor gravado
 * enquanto a resposta chega.
 */
function FormularioDeEdicaoDeOrigem({ acao }: FormularioDeEdicaoDeOrigemProps) {
  const salvar = useEditarOrigem(acao.id);
  const { areasAtivas } = useResolvedores();

  const [assunto, definirAssunto] = useState(acao.assunto);
  const [descricao, definirDescricao] = useState(acao.descricao ?? '');
  const [planoAcao, definirPlanoAcao] = useState(acao.planoAcao ?? '');
  const [areaSolicitanteId, definirAreaSolicitante] = useState<number | ''>(
    acao.areaSolicitanteId ?? '',
  );
  const [areaSesmt, definirAreaSesmt] = useState<AreaSesmt | ''>(acao.areaSesmt ?? '');
  const [modulo, definirModulo] = useState<ModuloDoSistema | ''>(acao.modulo ?? '');

  const assuntoVazio = assunto.trim() === '';
  const semModulo = modulo === '';

  const mudou =
    assunto.trim() !== acao.assunto ||
    (descricao.trim() === '' ? null : descricao.trim()) !== acao.descricao ||
    (planoAcao.trim() === '' ? null : planoAcao.trim()) !== acao.planoAcao ||
    (areaSolicitanteId === '' ? null : areaSolicitanteId) !== acao.areaSolicitanteId ||
    (areaSesmt === '' ? null : areaSesmt) !== acao.areaSesmt ||
    modulo !== acao.modulo;

  const salvarAgora = () => {
    if (assuntoVazio || semModulo) {
      return;
    }

    salvar.mutate({
      assunto: assunto.trim(),
      descricao: descricao.trim() === '' ? null : descricao.trim(),
      planoAcao: planoAcao.trim() === '' ? null : planoAcao.trim(),
      areaSolicitanteId: areaSolicitanteId === '' ? null : Number(areaSolicitanteId),
      areaSesmt: areaSesmt === '' ? null : areaSesmt,
      modulo,
    });
  };

  return (
    <div className={estilos.formularioDeOrigem}>
      <div className={estilos.campo}>
        <label className={estilos.rotulo} htmlFor={`assunto-${String(acao.id)}`}>
          Assunto
        </label>
        <input
          id={`assunto-${String(acao.id)}`}
          className={estilos.entrada}
          value={assunto}
          maxLength={200}
          disabled={salvar.isPending}
          aria-invalid={assuntoVazio}
          onChange={(evento) => {
            definirAssunto(evento.target.value);
          }}
        />
        {assuntoVazio && <p className={estilos.aviso}>O assunto não pode ficar vazio.</p>}
      </div>

      <div className={estilos.campo}>
        <label className={estilos.rotulo} htmlFor={`descricao-${String(acao.id)}`}>
          Descrição
        </label>
        <textarea
          id={`descricao-${String(acao.id)}`}
          className={estilos.area}
          value={descricao}
          disabled={salvar.isPending}
          onChange={(evento) => {
            definirDescricao(evento.target.value);
          }}
        />
      </div>

      <div className={estilos.campo}>
        <label className={estilos.rotulo} htmlFor={`plano-${String(acao.id)}`}>
          Plano de ação
        </label>
        <textarea
          id={`plano-${String(acao.id)}`}
          className={estilos.area}
          value={planoAcao}
          disabled={salvar.isPending}
          onChange={(evento) => {
            definirPlanoAcao(evento.target.value);
          }}
        />
      </div>

      <div className={estilos.campo}>
        <label className={estilos.rotulo} htmlFor={`area-solicitante-${String(acao.id)}`}>
          Área solicitante
        </label>
        <select
          id={`area-solicitante-${String(acao.id)}`}
          className={estilos.entrada}
          value={areaSolicitanteId}
          disabled={salvar.isPending}
          onChange={(evento) => {
            definirAreaSolicitante(evento.target.value === '' ? '' : Number(evento.target.value));
          }}
        >
          <option value="">Nenhuma</option>
          {areasAtivas.map((area) => (
            <option key={area.id} value={area.id}>
              {area.nome}
            </option>
          ))}
        </select>
      </div>

      <div className={estilos.campo}>
        <label className={estilos.rotulo} htmlFor={`area-sesmt-${String(acao.id)}`}>
          Área SESMT
        </label>
        <select
          id={`area-sesmt-${String(acao.id)}`}
          className={estilos.entrada}
          value={areaSesmt}
          disabled={salvar.isPending}
          onChange={(evento) => {
            definirAreaSesmt(evento.target.value as AreaSesmt | '');
          }}
        >
          <option value="">Nenhuma</option>
          {areasSesmtEmOrdem.map((opcao) => (
            <option key={opcao} value={opcao}>
              {rotuloDaAreaSesmt[opcao]}
            </option>
          ))}
        </select>
      </div>

      <div className={estilos.campo}>
        <label className={estilos.rotulo} htmlFor={`modulo-${String(acao.id)}`}>
          Módulo
        </label>
        <select
          id={`modulo-${String(acao.id)}`}
          className={estilos.entrada}
          value={modulo}
          disabled={salvar.isPending}
          aria-invalid={semModulo}
          onChange={(evento) => {
            definirModulo(evento.target.value as ModuloDoSistema | '');
          }}
        >
          <option value="">Selecione</option>
          {modulosEmOrdem.map((opcao) => (
            <option key={opcao} value={opcao}>
              {rotuloDoModulo[opcao]}
            </option>
          ))}
        </select>
        {semModulo && <p className={estilos.aviso}>O módulo é obrigatório.</p>}
      </div>

      <div className={estilos.acoes}>
        <button
          type="button"
          className={estilos.botao}
          disabled={!mudou || assuntoVazio || semModulo || salvar.isPending}
          onClick={salvarAgora}
        >
          {salvar.isPending ? 'Salvando...' : 'Salvar alterações'}
        </button>
      </div>

      {salvar.error !== null && (
        <p className={estilos.falha} role="alert">
          {descreverErro(salvar.error)}
        </p>
      )}
    </div>
  );
}

interface FormularioDeProcessoProps {
  acao: AcaoDetalhada;
}

/**
 * Edicao do grupo Processo: Responsavel, Prazo e, so quando a area responsavel e do
 * tipo Produto, Prioridade, PO, Dev, Qa, Complexidade, Financiada e Encaixe.
 *
 * QUATRO SALVAMENTOS INDEPENDENTES, e nao um botao "Salvar" unico. Responsavel, Prazo e
 * os dados de produto sao TRES rotas distintas no servidor (cada uma com sua regra:
 * motivo obrigatorio ao repactuar prazo, exclusividade de area Produto nos dados de
 * produto), e um botao so exigiria juntar os tres resultados de erro numa unica
 * mensagem — pior do que apontar exatamente qual campo falhou. Mesmo padrao de
 * `CampoDeTask`, que ja salva a task isoladamente dos outros campos do grupo.
 *
 * `key={acao.atualizadoEm}` no componente pai remonta este formulario a cada gravacao
 * confirmada, pelo mesmo motivo de `CampoDeTask` e `FormularioDeEdicaoDeOrigem`.
 */
function FormularioDeProcesso({ acao }: FormularioDeProcessoProps) {
  const ehDeProduto = acao.tipoAreaResponsavel === 'Produto';

  return (
    <div className={estilos.formularioDeOrigem}>
      <CampoDeResponsavel acao={acao} />
      <CampoDePrazo acao={acao} />

      {ehDeProduto && (
        <>
          <CampoDePrioridade acao={acao} />
          <CampoDeDadosDeProduto acao={acao} />
        </>
      )}

      {/*
        A correcao de assunto fica no FIM do grupo Processo, e nao dentro de
        FormularioDeEdicaoDeOrigem: ela so aparece quando o grupo ORIGEM esta fechado — e o
        unico momento em que faz sentido, porque enquanto Origem esta aberto o caminho
        normal (editar origem) resolve. Exigiria acao.gruposEditaveis aqui para checar isso
        de verdade; a permissao `acao.corrigirAssunto` ja decide por outro caminho — quem
        nao a tem simplesmente nao ve o campo mudar de leitura para edicao, porque
        `CampoDeAssunto` sempre tenta a mutation e o servidor responde 403 so para quem
        tentar sem a permissao.
      */}
      {!acao.gruposEditaveis.includes('Origem') && <CampoDeAssunto acao={acao} />}
    </div>
  );
}

interface CampoDeAssuntoProps {
  acao: AcaoDetalhada;
}

/**
 * Correcao pontual do Assunto, fora do grupo Origem. Excecao a Decisao 16.
 *
 * MOTIVO SEMPRE OBRIGATORIO, diferente de `CampoDePrazo`: ali so o segundo prazo em
 * diante exige motivo, porque definir o primeiro nao desfaz nada. Aqui toda correcao
 * desfaz o que o CS escreveu, entao nao ha "primeira vez sem motivo".
 */
function CampoDeAssunto({ acao }: CampoDeAssuntoProps) {
  const salvar = useCorrigirAssunto(acao.id);

  const [assunto, definirAssunto] = useState(acao.assunto);
  const [motivo, definirMotivo] = useState('');

  const assuntoVazio = assunto.trim() === '';
  const motivoVazio = motivo.trim() === '';
  const mudou = assunto.trim() !== acao.assunto;

  return (
    <div className={estilos.campo}>
      <label className={estilos.rotulo} htmlFor={`corrigir-assunto-${String(acao.id)}`}>
        Título do card
      </label>
      <p className={estilos.ajuda}>
        Corrige o título fora do fluxo normal de edição. É excepcional: o conteúdo de origem
        pertence ao CS, e esta correção precisa de motivo porque desfaz o que ele escreveu.
      </p>

      <input
        id={`corrigir-assunto-${String(acao.id)}`}
        className={estilos.entrada}
        value={assunto}
        maxLength={200}
        disabled={salvar.isPending}
        aria-invalid={assuntoVazio}
        onChange={(evento) => {
          definirAssunto(evento.target.value);
        }}
      />
      {assuntoVazio && <p className={estilos.aviso}>O título não pode ficar vazio.</p>}

      {mudou && (
        <div className={estilos.campo}>
          <label className={estilos.rotulo} htmlFor={`motivo-correcao-assunto-${String(acao.id)}`}>
            Motivo da correção
          </label>
          <input
            id={`motivo-correcao-assunto-${String(acao.id)}`}
            className={estilos.entrada}
            value={motivo}
            disabled={salvar.isPending}
            onChange={(evento) => {
              definirMotivo(evento.target.value);
            }}
          />
        </div>
      )}

      <div className={estilos.acoes}>
        <button
          type="button"
          className={estilos.botao}
          disabled={!mudou || assuntoVazio || motivoVazio || salvar.isPending}
          onClick={() => {
            salvar.mutate({ assunto: assunto.trim(), motivo: motivo.trim() });
          }}
        >
          {salvar.isPending ? 'Salvando...' : 'Corrigir título'}
        </button>
      </div>

      {salvar.error !== null && (
        <p className={estilos.falha} role="alert">
          {descreverErro(salvar.error)}
        </p>
      )}
    </div>
  );
}

interface CampoDeResponsavelProps {
  acao: AcaoDetalhada;
}

function CampoDeResponsavel({ acao }: CampoDeResponsavelProps) {
  const salvar = useDefinirResponsavel(acao.id);
  const { usuariosAtivos } = useResolvedores();

  const [responsavelId, definirResponsavelId] = useState<number | ''>(acao.responsavelId ?? '');

  const mudou = (responsavelId === '' ? null : responsavelId) !== acao.responsavelId;

  return (
    <div className={estilos.campo}>
      <label className={estilos.rotulo} htmlFor={`responsavel-${String(acao.id)}`}>
        Responsável
      </label>

      <div className={estilos.acoes}>
        <select
          id={`responsavel-${String(acao.id)}`}
          className={estilos.entrada}
          value={responsavelId}
          disabled={salvar.isPending}
          onChange={(evento) => {
            definirResponsavelId(evento.target.value === '' ? '' : Number(evento.target.value));
          }}
        >
          <option value="">Nenhum</option>
          {usuariosAtivos.map((usuario) => (
            <option key={usuario.id} value={usuario.id}>
              {usuario.nome}
            </option>
          ))}
        </select>

        <button
          type="button"
          className={estilos.botao}
          disabled={!mudou || salvar.isPending}
          onClick={() => {
            salvar.mutate({ responsavelId: responsavelId === '' ? null : responsavelId });
          }}
        >
          {salvar.isPending ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      {salvar.error !== null && (
        <p className={estilos.falha} role="alert">
          {descreverErro(salvar.error)}
        </p>
      )}
    </div>
  );
}

interface CampoDePrazoProps {
  acao: AcaoDetalhada;
}

/**
 * Campo de prazo, com motivo obrigatorio ao REPACTUAR.
 *
 * O campo de motivo so aparece quando ja existe um prazo gravado (`acao.prazo !== null`)
 * e a pessoa mudou o valor: definir o primeiro prazo nao exige motivo, so trocar um que
 * ja foi acordado. A tela antecipa essa exigencia para nao gastar uma volta ao servidor
 * so para descobrir que falta o motivo.
 */
function CampoDePrazo({ acao }: CampoDePrazoProps) {
  const salvar = useDefinirPrazo(acao.id);

  const [prazo, definirPrazoDigitado] = useState(acao.prazo ?? '');
  const [motivo, definirMotivo] = useState('');

  const mudou = (prazo === '' ? null : prazo) !== acao.prazo;
  const repactuando = acao.prazo !== null && mudou;
  const motivoVazio = motivo.trim() === '';

  return (
    <div className={estilos.campo}>
      <label className={estilos.rotulo} htmlFor={`prazo-${String(acao.id)}`}>
        Prazo
      </label>

      <div className={estilos.acoes}>
        <input
          id={`prazo-${String(acao.id)}`}
          className={estilos.entrada}
          type="date"
          value={prazo}
          disabled={salvar.isPending}
          onChange={(evento) => {
            definirPrazoDigitado(evento.target.value);
          }}
        />

        <button
          type="button"
          className={estilos.botao}
          disabled={!mudou || salvar.isPending || (repactuando && motivoVazio)}
          onClick={() => {
            salvar.mutate({
              prazo: prazo === '' ? null : prazo,
              ...(motivo.trim() === '' ? {} : { motivo: motivo.trim() }),
            });
          }}
        >
          {salvar.isPending ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      {repactuando && (
        <div className={estilos.campo}>
          <label className={estilos.rotulo} htmlFor={`motivo-prazo-${String(acao.id)}`}>
            Motivo da repactuação
          </label>
          <input
            id={`motivo-prazo-${String(acao.id)}`}
            className={estilos.entrada}
            value={motivo}
            disabled={salvar.isPending}
            onChange={(evento) => {
              definirMotivo(evento.target.value);
            }}
          />
          <p className={estilos.aviso}>
            Alterar um prazo já definido exige dizer por que ele mudou.
          </p>
        </div>
      )}

      {salvar.error !== null && (
        <p className={estilos.falha} role="alert">
          {descreverErro(salvar.error)}
        </p>
      )}
    </div>
  );
}

interface CampoDePrioridadeProps {
  acao: AcaoDetalhada;
}

function CampoDePrioridade({ acao }: CampoDePrioridadeProps) {
  const salvar = useDefinirPrioridade(acao.id);

  const [prioridade, definirPrioridadeEscolhida] = useState<PrioridadeAcao | ''>(
    acao.prioridade ?? '',
  );

  const mudou = (prioridade === '' ? null : prioridade) !== acao.prioridade;
  const opcoes: PrioridadeAcao[] = ['Baixa', 'Media', 'Alta', 'Critica'];

  return (
    <div className={estilos.campo}>
      <label className={estilos.rotulo} htmlFor={`prioridade-${String(acao.id)}`}>
        Prioridade
      </label>

      <div className={estilos.acoes}>
        <select
          id={`prioridade-${String(acao.id)}`}
          className={estilos.entrada}
          value={prioridade}
          disabled={salvar.isPending}
          onChange={(evento) => {
            definirPrioridadeEscolhida(evento.target.value as PrioridadeAcao | '');
          }}
        >
          <option value="">Nenhuma</option>
          {opcoes.map((opcao) => (
            <option key={opcao} value={opcao}>
              {rotuloDaPrioridade[opcao]}
            </option>
          ))}
        </select>

        <button
          type="button"
          className={estilos.botao}
          disabled={!mudou || salvar.isPending}
          onClick={() => {
            salvar.mutate(prioridade === '' ? null : prioridade);
          }}
        >
          {salvar.isPending ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      {salvar.error !== null && (
        <p className={estilos.falha} role="alert">
          {descreverErro(salvar.error)}
        </p>
      )}
    </div>
  );
}

interface CampoDeDadosDeProdutoProps {
  acao: AcaoDetalhada;
}

/**
 * PO, Dev, Qa, Complexidade, Financiada e Encaixe — os seis campos exclusivos de area
 * responsavel do tipo Produto.
 *
 * UM UNICO "SALVAR" PARA OS SEIS, diferente de Responsavel/Prazo/Prioridade: os seis
 * campos vao juntos na mesma rota do servidor (`PUT .../dados-de-produto`), porque sao
 * conceitualmente um bloco so — nao ha vantagem em seis chamadas separadas para seis
 * campos que o dominio ja trata como uma unica operacao.
 */
function CampoDeDadosDeProduto({ acao }: CampoDeDadosDeProdutoProps) {
  const salvar = useDefinirDadosDeProduto(acao.id);

  const [po, definirPo] = useState(acao.po ?? '');
  const [dev, definirDev] = useState(acao.dev ?? '');
  const [qa, definirQa] = useState(acao.qa ?? '');
  const [complexidade, definirComplexidade] = useState<ComplexidadeAcao | ''>(
    acao.complexidade ?? '',
  );
  const [financiada, definirFinanciada] = useState(acao.financiada);
  const [encaixe, definirEncaixe] = useState(acao.encaixe);

  const mudou =
    (po.trim() === '' ? null : po.trim()) !== acao.po ||
    (dev.trim() === '' ? null : dev.trim()) !== acao.dev ||
    (qa.trim() === '' ? null : qa.trim()) !== acao.qa ||
    (complexidade === '' ? null : complexidade) !== acao.complexidade ||
    financiada !== acao.financiada ||
    encaixe !== acao.encaixe;

  const opcoesDeComplexidade: ComplexidadeAcao[] = ['Alta', 'Media', 'Baixa'];

  return (
    <div className={estilos.formularioDeOrigem}>
      <div className={estilos.campo}>
        <label className={estilos.rotulo} htmlFor={`po-${String(acao.id)}`}>
          PO
        </label>
        <input
          id={`po-${String(acao.id)}`}
          className={estilos.entrada}
          value={po}
          disabled={salvar.isPending}
          onChange={(evento) => {
            definirPo(evento.target.value);
          }}
        />
      </div>

      <div className={estilos.campo}>
        <label className={estilos.rotulo} htmlFor={`dev-${String(acao.id)}`}>
          Dev
        </label>
        <input
          id={`dev-${String(acao.id)}`}
          className={estilos.entrada}
          value={dev}
          disabled={salvar.isPending}
          onChange={(evento) => {
            definirDev(evento.target.value);
          }}
        />
      </div>

      <div className={estilos.campo}>
        <label className={estilos.rotulo} htmlFor={`qa-${String(acao.id)}`}>
          Qa
        </label>
        <input
          id={`qa-${String(acao.id)}`}
          className={estilos.entrada}
          value={qa}
          disabled={salvar.isPending}
          onChange={(evento) => {
            definirQa(evento.target.value);
          }}
        />
      </div>

      <div className={estilos.campo}>
        <label className={estilos.rotulo} htmlFor={`complexidade-${String(acao.id)}`}>
          Complexidade
        </label>
        <select
          id={`complexidade-${String(acao.id)}`}
          className={estilos.entrada}
          value={complexidade}
          disabled={salvar.isPending}
          onChange={(evento) => {
            definirComplexidade(evento.target.value as ComplexidadeAcao | '');
          }}
        >
          <option value="">Nenhuma</option>
          {opcoesDeComplexidade.map((opcao) => (
            <option key={opcao} value={opcao}>
              {rotuloDaComplexidade[opcao]}
            </option>
          ))}
        </select>
      </div>

      <div className={estilos.campo}>
        <label className={estilos.opcaoBooleana}>
          <input
            type="checkbox"
            checked={financiada}
            disabled={salvar.isPending}
            onChange={(evento) => {
              definirFinanciada(evento.target.checked);
            }}
          />
          Financiada
        </label>
      </div>

      <div className={estilos.campo}>
        <label className={estilos.opcaoBooleana}>
          <input
            type="checkbox"
            checked={encaixe}
            disabled={salvar.isPending}
            onChange={(evento) => {
              definirEncaixe(evento.target.checked);
            }}
          />
          Encaixe
        </label>
      </div>

      <div className={estilos.acoes}>
        <button
          type="button"
          className={estilos.botao}
          disabled={!mudou || salvar.isPending}
          onClick={() => {
            salvar.mutate({
              po: po.trim() === '' ? null : po.trim(),
              dev: dev.trim() === '' ? null : dev.trim(),
              qa: qa.trim() === '' ? null : qa.trim(),
              complexidade: complexidade === '' ? null : complexidade,
              financiada,
              encaixe,
            });
          }}
        >
          {salvar.isPending ? 'Salvando...' : 'Salvar dados de produto'}
        </button>
      </div>

      {salvar.error !== null && (
        <p className={estilos.falha} role="alert">
          {descreverErro(salvar.error)}
        </p>
      )}
    </div>
  );
}

interface CampoDeTaskProps {
  acao: AcaoDetalhada;
  editavel: boolean;
}

/**
 * Campo de escrita da task do Azure DevOps.
 *
 * ACEITA COLAR A URL INTEIRA, e nao apenas o numero. Quem esta com o work item aberto no
 * navegador copia o endereco, nao o id: exigir que a pessoa extraia o numero na mao seria
 * criar trabalho para depois reclamar do erro de digitacao. O numero e extraido do fim da
 * URL e so ele vai para o servidor.
 *
 * O ESTADO LOCAL NASCE DO VALOR GRAVADO e e sincronizado por `key`, no componente pai — e
 * nao por efeito. Efeito para copiar prop em estado produz um render extra e desalinha o
 * campo enquanto a resposta chega.
 */
function CampoDeTask({ acao, editavel }: CampoDeTaskProps) {
  const salvar = useDefinirWorkItem(acao.id);
  const { urlDoWorkItem } = useResolvedores();

  const [digitado, definirDigitado] = useState(acao.workItem === null ? '' : String(acao.workItem));

  const numero = extrairNumeroDaTask(digitado);
  const vazio = digitado.trim() === '';

  // Vazio e intencao de desvincular. Texto que nao rende numero e erro de digitacao, e
  // desabilitar e melhor do que aceitar e receber 409.
  const podeSalvar = !salvar.isPending && (vazio || numero !== null);
  const mudou = (numero ?? null) !== acao.workItem;

  if (!editavel) {
    return <CampoDoWorkItem numero={acao.workItem} url={urlDoWorkItem(acao.workItem)} />;
  }

  return (
    <div>
      <label className={estilos.rotulo} htmlFor={`task-${String(acao.id)}`}>
        Task no Azure DevOps
      </label>

      <div className={estilos.acoes}>
        <input
          id={`task-${String(acao.id)}`}
          className={estilos.area}
          type="text"
          inputMode="numeric"
          placeholder="53157 ou o endereço do work item"
          value={digitado}
          disabled={salvar.isPending}
          onChange={(evento) => {
            definirDigitado(evento.target.value);
          }}
        />

        <button
          type="button"
          className={estilos.botao}
          disabled={!podeSalvar || !mudou}
          onClick={() => {
            salvar.mutate({ workItem: numero });
          }}
        >
          {salvar.isPending ? 'Salvando...' : acao.workItem === null ? 'Vincular' : 'Alterar'}
        </button>

        {acao.workItem !== null && (
          <button
            type="button"
            className={estilos.botaoDestrutivo}
            disabled={salvar.isPending}
            onClick={() => {
              definirDigitado('');
              salvar.mutate({ workItem: null });
            }}
          >
            Desvincular
          </button>
        )}
      </div>

      {/* Erro de digitacao avisado antes de tentar, em vez de virar 409 do servidor. */}
      {!vazio && numero === null && (
        <p className={estilos.aviso}>
          Informe o número da task ou cole o endereço dela no Azure DevOps.
        </p>
      )}

      {acao.workItem !== null && (
        <p className={estilos.texto}>
          Vinculada:{' '}
          <CampoDoWorkItem numero={acao.workItem} url={urlDoWorkItem(acao.workItem)} somenteValor />
        </p>
      )}

      {salvar.error !== null && (
        <p className={estilos.falha} role="alert">
          {descreverErro(salvar.error)}
        </p>
      )}
    </div>
  );
}

/**
 * Extrai o numero da task de texto livre.
 *
 * Aceita `53157`, ` 53157 `, `#53157` e o endereco completo
 * `https://dev.azure.com/org/projeto/_workitems/edit/53157`. Devolve `null` quando nao ha
 * numero positivo — inclusive para `0` e negativos, que o dominio recusa.
 */
function extrairNumeroDaTask(texto: string): number | null {
  const limpo = texto.trim();

  if (limpo === '') {
    return null;
  }

  // O ultimo grupo de digitos: numa URL e o id, e num texto solto e o proprio numero.
  const encontrado = /(\d+)(?!.*\d)/.exec(limpo);

  if (encontrado === null) {
    return null;
  }

  const numero = Number(encontrado[1]);

  return Number.isSafeInteger(numero) && numero > 0 ? numero : null;
}

interface CampoDoWorkItemProps {
  numero: number | null;
  url: string | null;
  /** Mostra so o valor, sem o rotulo. Para uso no meio de uma frase. */
  somenteValor?: boolean;
}

/**
 * Work item do Azure DevOps.
 *
 * Tres estados, e os tres precisam ser distinguíveis:
 *
 * sem work item        travessão, como qualquer campo vazio
 * com work item e URL  link que abre o item no Azure
 * com work item, sem URL  número em texto, quando a integração não está configurada
 *
 * O terceiro caso existe porque a URL vem de configuração: sem ela, montar um endereço
 * adivinhado levaria a página de erro, e o usuário culparia o número.
 */
function CampoDoWorkItem({ numero, url, somenteValor = false }: CampoDoWorkItemProps) {
  // `rel="noreferrer"` junto de `target`: sem ele a página aberta recebe acesso a
  // `window.opener` e pode navegar esta aba para outro endereço.
  const valor =
    numero === null ? (
      '—'
    ) : url === null ? (
      `#${String(numero)}`
    ) : (
      <a href={url} target="_blank" rel="noreferrer">
        #{String(numero)}
      </a>
    );

  if (somenteValor) {
    return <>{valor}</>;
  }

  return (
    <div>
      <dt className={estilos.rotulo}>Task no Azure</dt>
      <dd className={estilos.valor}>{valor}</dd>
    </div>
  );
}

interface TextoProps {
  conteudo: string | null;
  ausente: string;
}

function Texto({ conteudo, ausente }: TextoProps) {
  if (conteudo === null || conteudo.trim() === '') {
    return <p className={`${estilos.texto} ${estilos.vazio}`}>{ausente}</p>;
  }

  return <p className={estilos.texto}>{conteudo}</p>;
}
